import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useGetBranches, useGetPaymentMethods } from "src/api/generic";
import {
  useGetEmployeeOutstandingLoans,
  useGetSalaryEmployee,
  useGetSalaryEmployees,
} from "src/api/salary/employees";
import { EmployeeOutstandingLoan } from "src/types/salary";
import LoanCard, { EventDeductionLine } from "./loan-card";
import {
  SalaryPayload,
  useAddSalarySalaryAttachment,
  useCreateSalarySalary,
  useGetSalarySalary,
  usePaySalarySalary,
  useUpdateSalarySalary,
} from "src/api/salary/salaries";
import { notificationsState } from "src/store/notifications";
import { APIErrorResponse } from "src/types/generic";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import { employmentGross } from "../salary-calculations";
import {
  salaryFormDefaultValues,
  SalaryFormContext,
  salaryFormSchema,
  SalaryFormValues,
} from "./constants";

const SalaryModal = ({
  open,
  onClose,
  onSaved,
  salaryId,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  salaryId?: number;
}) => {
  const { t } = useTranslation("salary");
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const isEdit = !!salaryId;

  const prevPondFilesRef = useRef<File[]>([]);
  const [pondKey, setPondKey] = useState(0);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [confirmEditOpen, setConfirmEditOpen] = useState(false);
  const pendingValuesRef = useRef<SalaryFormValues | null>(null);

  // APIs
  const { data: salaryRes } = useGetSalarySalary(salaryId ?? null, open && isEdit);
  const detail = salaryRes?.data;
  const { data: branchesRes } = useGetBranches();
  const branches = branchesRes?.data ?? [];
  const { data: employeesRes } = useGetSalaryEmployees({
    tab: "active",
    page: 1,
    search: "",
  });
  const employees = employeesRes?.data ?? [];
  const { data: pmRes } = useGetPaymentMethods({ page: 1, enabled: open });
  const paymentMethods = pmRes?.data ?? [];

  const { mutateAsync: createMut, isPending: creating } =
    useCreateSalarySalary();
  const { mutateAsync: updateMut, isPending: updating } =
    useUpdateSalarySalary();
  const { mutateAsync: payMut, isPending: paying } = usePaySalarySalary();
  const { mutateAsync: uploadAttachment, isPending: uploadingAttachments } =
    useAddSalarySalaryAttachment();

  const busy = creating || updating || paying || uploadingAttachments;
  const existingPayments = detail?.payments ?? [];

  // The schema's payment validation reads availableToPay via this ref, which is
  // refreshed below on every render with the latest derived value.
  const contextRef = useRef<SalaryFormContext>({ availableToPay: 0 });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<SalaryFormValues>({
    mode: "onBlur",
    resolver: yupResolver(salaryFormSchema),
    context: contextRef.current,
    defaultValues: salaryFormDefaultValues,
  });

  const { fields: paymentFields, append: appendPayment, remove: removePayment } = useFieldArray({
    name: "payments",
    control,
  });

  const employeeId = Number(watch("employee_id") || 0);
  const additionalPay = Number(watch("additional_pay") || 0);
  const deduction = Number(watch("deduction") || 0);
  const payments = watch("payments") ?? [];

  const [eventDeductions, setEventDeductions] = useState<EventDeductionLine[]>(
    [],
  );

  const { data: loansRes } = useGetEmployeeOutstandingLoans(
    employeeId > 0 ? employeeId : null,
    open && employeeId > 0,
  );
  const rawOutstandingLoans: EmployeeOutstandingLoan[] = loansRes?.data ?? [];

  // In edit mode, the API already subtracted this salary's deductions from
  // remaining_balance. Add them back so the user sees the full available
  // balance for each loan, and include loans fully covered by this salary
  // (they won't appear in outstanding loans otherwise).
  const outstandingLoans: EmployeeOutstandingLoan[] = useMemo(() => {
    if (!isEdit || !detail?.event_deductions?.length) return rawOutstandingLoans;

    const existingDeductions: { purchase_event_id: number; amount: number; purchase_event_reference: string }[] =
      detail.event_deductions;

    const merged = new Map<number, EmployeeOutstandingLoan>();

    for (const loan of rawOutstandingLoans) {
      merged.set(loan.id, { ...loan });
    }

    for (const ded of existingDeductions) {
      const dedAmount = Number(String(ded.amount).replace(/,/g, "")) || 0;
      const existing = merged.get(ded.purchase_event_id);
      if (existing) {
        existing.remaining_balance += dedAmount;
        existing.already_deducted -= dedAmount;
      } else {
        merged.set(ded.purchase_event_id, {
          id: ded.purchase_event_id,
          reference: ded.purchase_event_reference,
          amount: dedAmount,
          already_deducted: 0,
          remaining_balance: dedAmount,
          date: null,
        });
      }
    }

    return Array.from(merged.values()).filter((l) => l.remaining_balance > 0);
  }, [rawOutstandingLoans, isEdit, detail?.event_deductions]);

  const { data: employeeRes, isLoading: selectedEmployeeDetailLoading } =
    useGetSalaryEmployee(
      employeeId > 0 ? employeeId : null,
      open && employeeId > 0,
    );
  const selectedEmployeeDetail = employeeRes?.data;
  const selectedEmployee = useMemo(
    () =>
      employees.find(
        (e: { id: number | string }) =>
          Number(e.id) > 0 && Number(e.id) === Number(employeeId),
      ),
    [employees, employeeId],
  );

  const effectiveCompensation = useMemo(() => {
    if (selectedEmployeeDetail) {
      const allowances = Array.isArray(selectedEmployeeDetail.allowances)
        ? selectedEmployeeDetail.allowances
        : [];
      const allowanceTotal = allowances.reduce(
        (sum: number, a: any) => sum + Number(a?.amount ?? 0),
        0,
      );
      return {
        basicSalary: Number(selectedEmployeeDetail.basic_salary || 0),
        allowanceTotal,
      };
    }
    if (selectedEmployee) {
      return {
        basicSalary: Number(selectedEmployee.basic_salary || 0),
        allowanceTotal: Number(selectedEmployee.total_allowance || 0),
      };
    }
    return null;
  }, [selectedEmployee, selectedEmployeeDetail]);

  const currentSalaryPreview = useMemo(() => {
    if (isEdit && detail) {
      return employmentGross(
        Number(detail.basic_salary ?? 0),
        Number(detail.allowance_total ?? 0),
      );
    }
    if (!effectiveCompensation) return 0;
    return employmentGross(
      Number(effectiveCompensation.basicSalary || 0),
      Number(effectiveCompensation.allowanceTotal || 0),
    );
  }, [detail, effectiveCompensation, isEdit]);

  const eventDeductionTotal = useMemo(
    () =>
      eventDeductions.reduce(
        (sum, d) => sum + (d.purchase_event_id ? Number(d.amount || 0) : 0),
        0,
      ),
    [eventDeductions],
  );

  const netSalaryToBePaid = useMemo(
    () =>
      Math.max(
        0,
        Number(currentSalaryPreview) +
          Number(additionalPay || 0) -
          Number(deduction || 0) -
          eventDeductionTotal,
      ),
    [currentSalaryPreview, additionalPay, deduction, eventDeductionTotal],
  );

  const availableToPay = useMemo(() => {
    const paidTotal = Number(isEdit ? detail?.paid_total ?? 0 : 0);
    return Math.max(0, netSalaryToBePaid - paidTotal);
  }, [detail?.paid_total, isEdit, netSalaryToBePaid]);

  const paymentsTotal = useMemo(
    () =>
      payments.reduce((sum, line) => {
        const amount = Number(line?.amount || 0);
        return sum + (Number.isFinite(amount) && amount > 0 ? amount : 0);
      }, 0),
    [payments],
  );

  const remainingCredit = useMemo(
    () => Math.max(0, availableToPay - paymentsTotal),
    [availableToPay, paymentsTotal],
  );

  contextRef.current.availableToPay = availableToPay;

  useEffect(() => {
    if (!open) return;
    const resetExtras = () => {
      setConfirmEditOpen(false);
      setEventDeductions([]);
      prevPondFilesRef.current = [];
      setPendingFiles([]);
      setPondKey((k) => k + 1);
    };
    if (!isEdit) {
      reset(salaryFormDefaultValues);
      resetExtras();
      return;
    }
    if (!detail) return;
    reset({
      ...salaryFormDefaultValues,
      branch_id: detail.branch?.id ?? (undefined as unknown as number),
      employee_id: detail.employee?.id ?? (undefined as unknown as number),
      additional_pay: detail.additional_pay ?? 0,
      deduction: detail.deduction ?? 0,
      payment_date: detail.payment_date ? new Date(detail.payment_date) : null,
      received_date: detail.received_date ? new Date(detail.received_date) : null,
      remind_me_at: detail.remind_me_at ? new Date(detail.remind_me_at) : null,
      note: detail.note ?? "",
      payments: [{ payment_method_id: null, amount: 0 }],
    });
    setConfirmEditOpen(false);
    prevPondFilesRef.current = [];
    setPendingFiles([]);
    setPondKey((k) => k + 1);
    if (detail.event_deductions?.length) {
      setEventDeductions(
        detail.event_deductions.map((d: any) => ({
          purchase_event_id: d.purchase_event_id,
          amount: Number(String(d.amount).replace(/,/g, "")) || 0,
        })),
      );
    } else {
      setEventDeductions([]);
    }
  }, [open, isEdit, detail, reset]);

  const onPondFilesChange = (files: File[]) => {
    const prev = prevPondFilesRef.current;
    prevPondFilesRef.current = files;
    const prevKeys = new Set(
      prev.map((f) => `${f.name}-${f.size}-${f.lastModified}`),
    );
    const added = files.filter(
      (f) => !prevKeys.has(`${f.name}-${f.size}-${f.lastModified}`),
    );
    if (added.length) {
      setPendingFiles((cur) => [...cur, ...added]);
    }
  };

  const pushError = (e: unknown) => {
    const err = e as APIErrorResponse & { message?: string };
    setNotifications([
      ...notifications,
      {
        type: "error",
        message:
          err?.data?.errors?.[0]?.value ??
          err?.message ??
          generalT("somethingWrong"),
      },
    ]);
  };

  const doSubmit = async (values: SalaryFormValues) => {
    if (!isEdit && Number(values.employee_id) > 0 && selectedEmployeeDetailLoading) {
      setNotifications([
        ...notifications,
        { type: "error", message: t("salaryCalculatingPleaseWait") },
      ]);
      return;
    }

    const validEventDeductions = eventDeductions
      .filter((d) => d.purchase_event_id && d.amount > 0)
      .map((d) => ({
        purchase_event_id: d.purchase_event_id as number,
        amount: d.amount,
      }));

    const payload: SalaryPayload = {
      branch_id: Number(values.branch_id),
      employee_id: Number(values.employee_id),
      type: "monthly",
      additional_pay: Number(values.additional_pay) || 0,
      deduction: Number(values.deduction) || 0,
      ...(validEventDeductions.length > 0 && {
        event_deductions: validEventDeductions,
      }),
      payment_date: values.payment_date
        ? moment(values.payment_date).format("YYYY-MM-DD")
        : null,
      received_date: values.received_date
        ? moment(values.received_date).format("YYYY-MM-DD")
        : null,
      remind_me_at: values.remind_me_at
        ? moment(values.remind_me_at).format("YYYY-MM-DD")
        : null,
      note: values.note || null,
    };

    const payableLines = (values.payments ?? [])
      .map((l) => ({
        payment_method_id: l.payment_method_id as number,
        amount: Number(l.amount || 0),
      }))
      .filter((l) => l.amount > 0);

    try {
      if (isEdit && salaryId) {
        await updateMut({
          id: salaryId,
          ...payload,
          payments: payableLines,
        });
        setNotifications([
          ...notifications,
          { type: "success", message: generalT("updatedSuccessfully") },
        ]);
        onSaved();
        return;
      }

      const res = await createMut(payload);
      const created = res?.data ?? res;
      const createdId: number | undefined =
        created?.id ?? created?.data?.id ?? undefined;

      // Upload attachments after create (best effort).
      if (createdId && pendingFiles.length) {
        for (const f of pendingFiles) {
          // eslint-disable-next-line no-await-in-loop
          await uploadAttachment({ salaryId: createdId, file: f });
        }
      }

      // Optional: pay now using payment lines (best effort).
      if (createdId) {
        for (const line of payableLines) {
          // eslint-disable-next-line no-await-in-loop
          await payMut({
            salaryId: createdId,
            payment_method_id: line.payment_method_id,
            amount: line.amount,
          });
        }
      }

      setNotifications([
        ...notifications,
        { type: "success", message: generalT("addedSuccessfully") },
      ]);
      onSaved();
    } catch (e) {
      pushError(e);
    }
  };

  const onValid: SubmitHandler<SalaryFormValues> = (values) => {
    if (isEdit) {
      pendingValuesRef.current = values;
      setConfirmEditOpen(true);
      return;
    }
    doSubmit(values);
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Stack
          component="form"
          onSubmit={handleSubmit(onValid)}
          spacing={3}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "537px" },
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "#FFF",
            border: "1px solid #D0D5DD",
            borderRadius: 1,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={0}
          >
            <Typography color="#101828" fontSize="20px" lineHeight="24px">
              {isEdit ? t("editSalary") : t("createSalary")}
            </Typography>
            <IconButton onClick={onClose} sx={{ p: 0 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Stack spacing={2}>
            <Controller
              name="branch_id"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  size="small"
                  select
                  label={generalT("branch")}
                  fullWidth
                  disabled={isEdit}
                  error={!!errors.branch_id}
                  helperText={
                    errors.branch_id
                      ? t(errors.branch_id.message as string)
                      : undefined
                  }
                >
                  {branches.map((b: any) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="employee_id"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  size="small"
                  select
                  label={t("selectAccount")}
                  fullWidth
                  disabled={isEdit}
                  error={!!errors.employee_id}
                  helperText={
                    errors.employee_id
                      ? t(errors.employee_id.message as string)
                      : undefined
                  }
                >
                  <MenuItem value="">{t("selectEmployee")}</MenuItem>
                  {employees.map((e: any) => (
                    <MenuItem key={Number(e.id)} value={Number(e.id)}>
                      {e.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <TextField
              size="small"
              type="number"
              label={t("currentSalary")}
              value={currentSalaryPreview}
              fullWidth
              disabled
            />

            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              <Controller
                name="additional_pay"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    type="number"
                    label={t("additionalPay")}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="deduction"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    type="number"
                    label={t("deduction")}
                    fullWidth
                  />
                )}
              />
            </Stack>

            <LoanCard
              loans={outstandingLoans}
              eventDeductions={eventDeductions}
              onChange={setEventDeductions}
            />

            <TextField
              size="small"
              type="number"
              label={t("netSalaryToBePaid")}
              value={netSalaryToBePaid}
              fullWidth
              disabled
            />

            <Stack spacing={1}>
              {isEdit && existingPayments.length > 0 && (
                <Box>
                  <Typography fontSize={14} fontWeight={600} mb={1}>
                    {t("existingPayments")}
                  </Typography>
                  <Stack spacing={1}>
                    {existingPayments.map((payment: any) => (
                      <Stack
                        key={payment.id}
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        gap={1}
                      >
                        <Typography fontSize={13} color="#667085">
                          {payment.payment_method?.name ?? t("paymentMethod")}
                        </Typography>
                        <Typography fontSize={13} color="#667085">
                          {Number(payment.amount || 0).toFixed(2)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              <Typography fontSize={14} fontWeight={600}>
                {isEdit ? t("newPayments") : generalT("paymentMethod")}
              </Typography>

              <Stack spacing={1}>
                {paymentFields.map((paymentField, index) => (
                  <Stack
                    key={paymentField.id}
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    gap={2}
                  >
                    <Controller
                      name={`payments.${index}.payment_method_id`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ""}
                          size="small"
                          select
                          label={generalT("paymentMethod")}
                          fullWidth
                        >
                          <MenuItem value="">
                            {generalT("paymentMethod")}
                          </MenuItem>
                          {paymentMethods.map((pm: any) => (
                            <MenuItem key={pm.id} value={pm.id}>
                              {pm.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                    <Controller
                      name={`payments.${index}.amount`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          type="number"
                          label={generalT("amount")}
                          sx={{ width: { xs: "100%", sm: 160 } }}
                        />
                      )}
                    />
                    {paymentFields.length > 1 && (
                      <IconButton
                        color="error"
                        onClick={() => removePayment(index)}
                        size="small"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>

              {errors.payments && (
                <Typography color="error.main" fontSize={13}>
                  {t((errors.payments as { message?: string }).message as string)}
                </Typography>
              )}

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Button
                  variant="text"
                  onClick={() => appendPayment({ payment_method_id: null, amount: 0 })}
                >
                  {t("addAnotherMethod")}
                </Button>
                <Typography color="#98A2B3" fontSize={13}>
                  {t("creditLabel", { amount: remainingCredit.toFixed(2) })}
                </Typography>
              </Stack>
            </Stack>

            <Controller
              name="received_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  value={field.value ?? null}
                  onChange={(date) => field.onChange(date ?? null)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                    },
                  }}
                  format="dd/MM/yyyy"
                  label={generalT("receivedDate")}
                />
              )}
            />

            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  size="small"
                  label={generalT("notes")}
                  fullWidth
                  multiline
                  minRows={3}
                />
              )}
            />

            <Box>
              <Typography fontSize={14} fontWeight={600} mb={1}>
                {generalT("attachments")}
              </Typography>
              {!busy && (
                <FilePondUploader
                  key={pondKey}
                  onUpload={onPondFilesChange}
                  maxFiles={3}
                  maxFileSize={3}
                  acceptedFileTypes={[
                    "image/*",
                    "application/pdf",
                    "text/csv",
                    "application/vnd.ms-excel",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  ]}
                />
              )}
              {pendingFiles.length > 0 && (
                <Typography mt={1} color="#98A2B3" fontSize={12}>
                  {t("attachmentsWillUploadAfterSave", {
                    count: pendingFiles.length,
                  })}
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack spacing={2} direction="row">
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={onClose}
            >
              {generalT("cancel")}
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={busy}
              disabled={
                !isEdit && employeeId > 0 && selectedEmployeeDetailLoading
              }
            >
              {isEdit ? generalT("save") : t("createSalary")}
            </LoadingButton>
          </Stack>
        </Stack>
      </Modal>

      <Dialog
        open={confirmEditOpen}
        onClose={() => setConfirmEditOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("confirmEditTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("confirmEditMessage")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEditOpen(false)}>
            {generalT("cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmEditOpen(false);
              if (pendingValuesRef.current) {
                doSubmit(pendingValuesRef.current);
              }
            }}
            disabled={busy}
          >
            {t("confirmSaveChanges")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SalaryModal;
